'use client'

import React, { useRef, useEffect, useCallback } from 'react'
import { SectionHeader } from '@/components/ui/SectionHeader'
import { featuresData } from '../_data/homepage'
import { ImageComponent } from '@/components/ui/ImageComponent'
import { Button } from '@/components/ui/Button'

interface FeaturesGridModulesProps {
    dict: any
}

export function FeaturesGridModules({ dict }: FeaturesGridModulesProps) {
    const sectionRefs = useRef<(HTMLDivElement | null)[]>([])
    const sidebarRefs = useRef<(HTMLDivElement | null)[]>([])
    const containerRef = useRef<HTMLDivElement>(null)
    const modules = featuresData.modules

    const renderIcon = (icon: any) => {
        if (typeof icon !== 'string') return icon
        if (icon.startsWith('<svg')) {
            return <div dangerouslySetInnerHTML={{ __html: icon }} className="text-[#7c38f6]" />
        }
        return (
            <div className="relative flex items-center justify-center overflow-hidden">
                <ImageComponent
                    src={icon}
                    alt={dict.features.iconAlt}
                    fill
                    unoptimized
                    className="object-contain"
                    showLoader={false}
                />
            </div>
        )
    }

    // Set active sidebar item via DOM — no React state, no re-renders
    const setActiveSidebar = useCallback((index: number) => {
        sidebarRefs.current.forEach((el, i) => {
            if (!el) return
            if (i === index) {
                el.setAttribute('data-active', 'true')
            } else {
                el.removeAttribute('data-active')
            }
        })
    }, [])

    // IntersectionObserver for scroll-based active section tracking (desktop only)
    useEffect(() => {
        if (typeof window === 'undefined' || window.innerWidth < 1024) return

        // Set first item active on mount
        const timerId = setTimeout(() => {
            setActiveSidebar(0)

            const observers: IntersectionObserver[] = []

            sectionRefs.current.forEach((ref, index) => {
                if (!ref) return
                const observer = new IntersectionObserver(
                    (entries) => {
                        entries.forEach((entry) => {
                            if (entry.isIntersecting) {
                                setActiveSidebar(index)
                            }
                        })
                    },
                    {
                        rootMargin: '-20% 0px -60% 0px',
                        threshold: 0,
                    }
                )
                observer.observe(ref)
                observers.push(observer)
            })

            return () => {
                observers.forEach((obs) => obs.disconnect())
            }
        }, 100)

        return () => clearTimeout(timerId)
    }, [modules.length, setActiveSidebar])

    // Click sidebar item → smooth scroll to section
    const handleSidebarClick = useCallback((index: number) => {
        setActiveSidebar(index)
        const target = sectionRefs.current[index]
        if (target) {
            const offset = 140
            const top = target.getBoundingClientRect().top + window.scrollY - offset
            window.scrollTo({ top, behavior: 'smooth' })
        }
    }, [setActiveSidebar])

    return (
        <div className="relative z-0 w-full bg-black" data-theme="dark">
            {/* Header Row */}
            <div className="container mx-auto px-6 pt-20 sm:pt-40 pb-0 md:px-10 lg:px-0">
                <div className="flex flex-col items-start justify-between gap-8 md:flex-row md:items-end">
                    <SectionHeader
                        title={featuresData.title}
                        subtitle={featuresData.subtitle}
                        align="left"
                        isDark
                    />
                    <div
                        className="flex shrink-0 items-center gap-[10px] border-none font-sans px-0 py-[14px]! text-[16px] font-medium text-white cursor-pointer"
                    >
                        {dict.common.viewAllFeatures}
                        <span><svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none">
                            <path d="M12.5511 4.47559V4.47744L11.4453 4.52538L12.5511 4.47559Z" fill="white" />
                            <path d="M22 11.1235V12.9253C19.3988 12.9253 17.5044 14.0021 16.3699 16.1251C15.4802 17.7899 15.3885 19.5076 15.3879 19.5244L13.6031 19.4445C13.6067 19.3598 13.7044 17.3533 14.7644 15.3324C15.1848 14.5294 15.7929 13.6622 16.6606 12.9253H2V11.1235H16.6606C15.7929 10.386 15.1854 9.5188 14.7644 8.71643C13.7044 6.69489 13.6067 4.68897 13.6031 4.60428L15.3879 4.52441C15.3879 4.54122 15.4802 6.25887 16.3699 7.92367C17.5044 10.0467 19.3988 11.1235 22 11.1235Z" fill="white" />
                        </svg></span>
                    </div>
                </div>
            </div>

            {/* Mobile Layout — All modules expanded */}
            <div className="px-6 pt-8 pb-8 lg:hidden">
                <div className="mx-auto">
                    <div className="relative flex flex-col gap-12">
                        {modules.map((mod) => (
                            <div
                                key={mod.id}
                                className="relative flex flex-col"
                            >
                                <h3 className="font-bricolage text-2xl font-bold text-white mb-3">
                                    {dict.modules[mod.id]?.title || mod.id}
                                </h3>
                                <p className="font-sans text-[15px] leading-relaxed font-medium text-[#918F9C] mb-5">
                                    {dict.modules[mod.id]?.tagline ||
                                        dict.modules[mod.id]?.description}
                                </p>

                                <div className="relative  w-full max-w-[844px] overflow-hidden rounded-3xl">
                                    <div className="relative aspect-16/13 w-full">
                                        <ImageComponent
                                            src={mod.homeImage}
                                            alt={mod.id}
                                            fill
                                            className="object-contain"
                                        />
                                    </div>
                                </div>
                            </div>
                        ))}
                    </div>
                </div>
            </div>

            {/* Desktop: Sticky Sidebar + Vertically Stacked Cards */}
            <div ref={containerRef} className="hidden lg:block">
                <div className="container mx-auto pt-16 pb-24">
                    <div className="grid grid-cols-12 gap-0">
                        {/* Sticky Sidebar — styled via data-active attribute + CSS */}
                        <div className="col-span-4">
                            <div className="sticky top-[140px]">
                                <div className="relative flex flex-col items-start gap-12 pl-6">
                                    {modules.map((mod, index) => (
                                        <div
                                            key={mod.id}
                                            ref={(el) => { sidebarRefs.current[index] = el }}
                                            data-active={index === 0 ? 'true' : undefined}
                                            className="sidebar-item relative flex cursor-pointer items-start gap-5 py-0"
                                            onClick={() => handleSidebarClick(index)}
                                        >
                                            <div className="flex max-w-[264px] flex-col">
                                                <div className="relative">
                                                    <div className="sidebar-indicator absolute -left-6 top-0 bottom-0 w-[4px] h-[34px] bg-[#A16DFF] gpu-accelerated" />
                                                    <h3 className="sidebar-title font-bricolage leading-tight font-bold">
                                                        {dict.modules[mod.id]?.title || mod.id}
                                                    </h3>
                                                </div>
                                                <div className="sidebar-description">
                                                    <p className="font-sans text-[16px] leading-normal font-medium text-[#918F9C]">
                                                        {dict.modules[mod.id]?.tagline ||
                                                            dict.modules[mod.id]?.description}
                                                    </p>
                                                </div>
                                            </div>
                                        </div>
                                    ))}
                                </div>
                            </div>
                        </div>

                        {/* Scrollable Content — Original Card Design Stacked Vertically */}
                        <div className="col-span-8 flex flex-col gap-16">
                            {modules.map((mod, index) => (
                                <div
                                    key={mod.id}
                                    ref={(el) => { sectionRefs.current[index] = el }}
                                    className="scroll-mt-[140px]"
                                >
                                    <div className="relative h-[560px] w-full max-w-[844px] overflow-hidden rounded-3xl">
                                        <div className="relative mt-2 aspect-16/10 w-full h-full">
                                            <ImageComponent
                                                src={mod.homeImage}
                                                alt={mod.id}
                                                fill
                                                className="object-contain"
                                            />
                                        </div>
                                    </div>
                                </div>
                            ))}
                        </div>
                    </div>
                </div>
            </div>
        </div>
    )
}
